import startCase from 'lodash/startCase';

import type { IChatMessage } from '@kognitos/lattice';

import type {
  IRainToolCallState,
  IRainToolRenderContext,
} from '../../hooks/types';
import { RainAutomationRunCard } from '../cards/RainAutomationRunCard';
import { RainRawToolCall } from '../../components/RainRawToolCall';
import {
  extractRunOutputs,
  extractRunState,
  isAutomationRunCompanionTool,
  isRunOutputsTool,
  readAutomationSlug,
} from '../automation-tools';

/**
 * Renderers for the `automations` tool source, as seen from inside a TARGET
 * agent's own chat — the agents Smith builds.
 *
 * These are matched by shape, not by name (`resolveToolRenderer`), because the
 * tool names are the workspace's published automation slugs and so are unknown
 * to this repo. See `automation-tools.ts` for the convention.
 */

/** `divide_two_numbers` -> `Divide Two Numbers`. */
const toAutomationName = (slug: string): string => startCase(slug);

/**
 * The BASE automation tool — the call that starts a run. Carries the run card.
 */
export function renderAutomationRunCard(
  state: IRainToolCallState,
  ctx: IRainToolRenderContext,
): IChatMessage {
  return ctx.renderCustomCard(
    state,
    <>
      <RainAutomationRunCard
        automationName={toAutomationName(state.name ?? '')}
        status={state.status}
        runState={extractRunState(state.result)}
        outputs={extractRunOutputs(state.result)}
        errorMessage={state.errorMessage}
      />
      {/* A run can change real records, so its exact inputs and outputs are
          worth being able to reach — the card shows the outcome, this shows
          what was actually sent and returned. */}
      <RainRawToolCall state={state} />
    </>,
  );
}

/**
 * A run-inspection companion (`__get_run_status` and friends).
 *
 * The agent polls these while a run is in flight, so one card per call would
 * bury the run itself under a stack of near-identical rows. The base tool's
 * card is the thing worth watching; these collapse to a single quiet line.
 *
 * `__get_run_outputs` is the exception — it carries the RESULT of the run, which
 * is the whole point of having run it, so it renders the full card.
 */
export function renderAutomationRunCompanionCard(
  state: IRainToolCallState,
  ctx: IRainToolRenderContext,
): IChatMessage {
  const slug = readAutomationSlug(state.name);
  const name = slug ? toAutomationName(slug) : '';

  if (isRunOutputsTool(state.name)) {
    return ctx.renderCustomCard(
      state,
      <>
        <RainAutomationRunCard
          automationName={name}
          status={state.status}
          runState={extractRunState(state.result) ?? 'COMPLETED'}
          outputs={extractRunOutputs(state.result)}
          errorMessage={state.errorMessage}
        />
        <RainRawToolCall state={state} />
      </>,
    );
  }

  return ctx.renderCustomCard(
    state,
    <>
      <RainAutomationRunCard
        automationName={name}
        status={state.status}
        runState={extractRunState(state.result)}
        // Polling calls report progress, not outcomes: their payload is the run's
        // state, which the card already shows in its title.
        errorMessage={state.errorMessage}
      />
      {/*
       * The raw call ONLY when the poll failed.
       *
       * A succeeding poll says nothing the title does not already say, and the
       * agent fires several per run — a disclosure on each would rebuild exactly
       * the noise this branch exists to suppress. A FAILING one is the opposite:
       * the card can only report that it failed, while the cause (a malformed
       * run name, a permission error) is in the arguments and the error payload.
       * That is the moment the raw call is worth the row.
       */}
      {state.status === 'error' && <RainRawToolCall state={state} />}
    </>,
  );
}

/**
 * Shape-based lookup for the automations family, for a profile's
 * `resolveToolRenderer`.
 *
 * Returns `undefined` for everything else so the caller can fall through to its
 * own defaults. NOTE the asymmetry: a run COMPANION is identifiable by shape (a
 * closed set of `__` suffixes), but a BASE automation tool is not — its name is
 * just the automation's slug, indistinguishable from any other tool name. So
 * only the companions resolve here; the base tools have to be named explicitly
 * by a profile that knows them (see `useAgentAutomationRenderers`).
 */
export function resolveAutomationToolRenderer(name: string | undefined) {
  return isAutomationRunCompanionTool(name)
    ? renderAutomationRunCompanionCard
    : undefined;
}

/** Re-exported for profiles that need the label without the renderer. */
export { toAutomationName };
